
import { useEffect } from 'react';
import { useVoiceCommands } from '@/contexts/VoiceCommandContext';
import { VoiceCommand } from '@/types/voiceCommand';

export interface AIAssistantVoiceHandlers {
  sendMessage?: (text: string) => void;
  toggleChat?: () => void;
  toggleSpeech?: () => void;
}

export const useAIAssistantVoiceCommands = (handlers: AIAssistantVoiceHandlers) => {
  const { registerCommand, unregisterCommand } = useVoiceCommands();
  const { sendMessage, toggleChat, toggleSpeech } = handlers;
  
  useEffect(() => {
    const assistantCommands: VoiceCommand[] = [
      {
        name: 'assistant-toggle',
        phrases: ['hey nova', 'hey assistant', 'assistant', 'open assistant', 'close assistant'],
        category: 'general',
        action: () => {
          if (toggleChat) {
            toggleChat();
          }
        },
        description: "Open or close the AI assistant",
        examples: ["Hey Nova", "Open assistant", "Close assistant"]
      }
    ];
    
    // Only register the ask command if sendMessage is provided
    if (sendMessage) {
      assistantCommands.push({
        name: 'assistant-ask',
        phrases: ['ask assistant', 'ask nova', 'tell assistant', 'tell nova'],
        category: 'general',
        action: (params) => {
          if (params) {
            sendMessage(params);
          }
        },
        description: "Ask a question to the AI assistant",
        examples: [
          "Ask assistant what's my streaming revenue",
          "Tell Nova schedule a recording session next Tuesday"
        ]
      });
    }
    
    // Only register the speech toggle command if toggleSpeech is provided
    if (toggleSpeech) {
      assistantCommands.push({
        name: 'assistant-toggle-speech',
        phrases: ['voice on', 'voice off', 'toggle voice', 'mute assistant', 'unmute assistant'],
        category: 'general',
        action: () => {
          toggleSpeech();
        },
        description: "Toggle the assistant's voice on or off",
        examples: ["Voice on", "Voice off", "Mute assistant"]
      });
    }
    
    // Register all commands
    assistantCommands.forEach(cmd => registerCommand(cmd));
    
    // Cleanup
    return () => {
      assistantCommands.forEach(cmd => unregisterCommand(cmd.name));
    };
  }, [registerCommand, unregisterCommand, sendMessage, toggleChat, toggleSpeech]);
};
